import React, { useCallback, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Skeleton, IconButton,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem,
  ListItemText, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../api';
import { API_CONFIG } from '../config';
import { useLanguage } from '../i18n/LanguageContext';

interface Insight {
  icon: string;
  message: string;
  type: string;
}

export interface InsightsResult {
  headline: string;
  insights: Insight[];
}

interface InsightsCardProps {
  /** True while the daily insights fetch is in flight. */
  loading: boolean;
  /** Resolved data, or null when unavailable/failed. */
  daily: InsightsResult | null;
  /** Called when the user clicks the refresh button. */
  onRefresh: () => void;
}

const InsightsCard: React.FC<InsightsCardProps> = ({ loading, daily, onRefresh }) => {
  const { t } = useLanguage();
  const [weekly, setWeekly] = useState<InsightsResult | null>(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyError, setWeeklyError] = useState(false);
  const [weeklyExpanded, setWeeklyExpanded] = useState(false);

  const fetchWeekly = useCallback((force = false) => {
    setWeeklyLoading(true);
    setWeeklyError(false);
    const endpoint = force
      ? API_CONFIG.endpoints.insights.weeklyForce
      : API_CONFIG.endpoints.insights.weekly;
    api.get(endpoint)
      .then(r => setWeekly(r.data))
      .catch(() => setWeeklyError(true))
      .finally(() => setWeeklyLoading(false));
  }, []);

  const handleWeeklyExpand = (_: React.SyntheticEvent, expanded: boolean) => {
    setWeeklyExpanded(expanded);
    if (expanded && !weekly && !weeklyLoading) {
      fetchWeekly();
    }
  };

  // Show a fallback card with retry when finished loading but have nothing to show.
  if (!loading && !daily) {
    return (
      <Card sx={{ borderRadius: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="text.secondary">{t.todaysInsights}</Typography>
            <Tooltip title={t.refreshInsights}>
              <span>
                <IconButton size="small" onClick={onRefresh} aria-label="refresh daily insights">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            {t.insightsUnavailable}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t.todaysInsights}
          </Typography>
          <Tooltip title={t.refreshInsights}>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                aria-label="refresh daily insights"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {loading ? (
          <Box>
            <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="95%" />
          </Box>
        ) : daily ? (
          <>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              {daily.headline}
            </Typography>
            <List dense disablePadding>
              {daily.insights.map((insight, i) => (
                <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                  <Typography variant="body2" sx={{ mr: 1, lineHeight: 1.5, fontSize: '1.1em' }}>
                    {insight.icon}
                  </Typography>
                  <ListItemText
                    primary={insight.message}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : null}

        <Accordion
          expanded={weeklyExpanded}
          onChange={handleWeeklyExpand}
          disableGutters
          elevation={0}
          sx={{ mt: 1, '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, minHeight: 'unset', '& .MuiAccordionSummary-content': { my: 0.5 } }}
          >
            <Typography variant="caption" color="text.secondary">
              {t.weeklyReport}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {weeklyLoading ? (
              <Box>
                <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
                {[1, 2, 3, 4].map(n => <Skeleton key={n} variant="text" width="95%" />)}
              </Box>
            ) : weeklyError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.disabled">
                  {t.weeklyInsightsUnavailable}
                </Typography>
                <Tooltip title={t.refreshWeeklyInsights}>
                  <span>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); fetchWeekly(true); }} aria-label="refresh weekly insights">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : weekly ? (
              <>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  {weekly.headline}
                </Typography>
                <List dense disablePadding>
                  {weekly.insights.map((insight, i) => (
                    <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                      <Typography variant="body2" sx={{ mr: 1, lineHeight: 1.5, fontSize: '1.1em' }}>
                        {insight.icon}
                      </Typography>
                      <ListItemText
                        primary={insight.message}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                  <Tooltip title={t.refreshWeeklyInsights}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); fetchWeekly(true); }}
                        disabled={weeklyLoading}
                        aria-label="refresh weekly insights"
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            ) : null}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default InsightsCard;
